import { css } from "@emotion/react"
import { FormDisabledProvider, TButton } from "@superwall-me/sui"
import { toast } from "app/core/components/hints"
import { SButton } from "@superwall-me/sui"
import { formatZodError } from "blitz"
import arrayMutators from "final-form-arrays"
import setFieldTouched from "final-form-set-field-touched"
import React, { PropsWithoutRef, ReactNode, useState } from "react"
import { Form as FinalForm, FormProps as FinalFormProps, FormSpy } from "react-final-form"
import tw from "tailwind-styled-components"
import { z } from "zod"
export { FORM_ERROR } from "final-form"

type FormToastContent = {
  loadingContent: string
  successContent: string
  errorContent: string
}

export interface FormProps<S extends z.ZodType<any, any>>
  extends Omit<PropsWithoutRef<JSX.IntrinsicElements["form"]>, "onSubmit"> {
  /** All your form fields */
  children?: ReactNode
  /** Text to display in the submit button */
  submitText?: string
  schema?: S
  decorators?: FinalFormProps<z.infer<S>>["decorators"]
  useDefaultToats?: boolean
  toastContent?: FormToastContent
  onSubmit: FinalFormProps<z.infer<S>>["onSubmit"]
  validate?: FinalFormProps<z.infer<S>>["validate"]
  initialValues?: FinalFormProps<z.infer<S>>["initialValues"]
  debug?: FinalFormProps<z.infer<S>>["debug"] | true
  disabled?: boolean
  validateOnBlur?: boolean
  buttonPosition?: "center" | "start" | "end"
  buttonFill?: boolean
  preventEnterSubmit?: boolean
  buttonProps?: TButton
  returnErrors?: boolean
}

const override = css`
  display: flex;
  align-items: center;
  justify-content: center;
`

const ButtonSection = tw.div<{ $buttonPosition: string; $buttonFill?: boolean }>`
  flex
  flex-row
  ${({ $buttonPosition }) => `justify-${$buttonPosition}`}
  ${({ $buttonFill }) => ($buttonFill === false ? "px-6" : "")}
  mt-2
`

export function Form<S extends z.ZodType<any, any>>({
  children,
  submitText,
  schema,
  initialValues,
  onSubmit,
  debug,
  toastContent,
  useDefaultToats,
  disabled,
  decorators,
  validateOnBlur = true,
  preventEnterSubmit = false,
  buttonPosition = "center",
  buttonFill = true,
  buttonProps = {},
  returnErrors = true,
  validate,
  ...props
}: FormProps<S>) {
  const [justSubmitted, setJustSubmitted] = useState<boolean>(false)
  const [toastId, setToastId] = useState<React.ReactText | undefined>(undefined)

  const { loadingContent, successContent, errorContent } = toastContent
    ? toastContent
    : { loadingContent: "Loading...", successContent: "Success!", errorContent: "Failure!" }
  const shouldShowToasts = toastContent || useDefaultToats

  const displayToast = () => {
    if (toastId && toastContent) {
      toast.error({ content: errorContent, toastId })
    }
  }

  const defaultValidation: FinalFormProps<z.infer<S>>["validate"] = (values) => {
    /**
     * If there's no schema passed in then don't validate
     */
    if (!schema) return {}

    try {
      schema.parse(values)
      return {}
    } catch (error) {
      const formattedErr = error && error.format ? formatZodError(error) : error.toString()
      return formattedErr
    }
  }
  const _validate = validate || defaultValidation

  return (
    <FormDisabledProvider isDisabled={!!disabled}>
      <FinalForm
        decorators={decorators}
        disabled={disabled}
        debug={
          debug === true
            ? ({ values }) => console.log(`values: ${JSON.stringify(values, null, 2)}`)
            : debug
        }
        initialValues={initialValues}
        validate={(values) => {
          const errors = _validate(values)
          // This was causing an error to show every time for some
          // reason.
          if (errors && Object.keys(errors).length > 0) {
            displayToast()
          }
          return errors
        }}
        validateOnBlur={validateOnBlur}
        onSubmit={onSubmit}
        mutators={{
          setFieldTouched,
          ...arrayMutators,
        }}
        render={({ values, handleSubmit, submitting, submitError }) => (
          <form
            onSubmit={(e) => {
              handleSubmit(e)
            }}
            className="form"
            {...props}
          >
            {shouldShowToasts && (
              <FormSpy
                subscription={{
                  hasSubmitErrors: true,
                  hasValidationErrors: true,
                  valid: true,
                  submitting: true,
                  submitFailed: true,
                  submitSucceeded: true,
                }}
                onChange={(props) => {
                  if (props.submitting) {
                    setJustSubmitted(true)
                    setToastId(toast.loading(loadingContent))
                  } else if (justSubmitted && props.submitSucceeded) {
                    toast.success({ content: successContent, toastId })
                    setJustSubmitted(false)
                    setToastId(undefined)
                  } else if (
                    justSubmitted &&
                    (props.submitFailed || props.hasValidationErrors || props.hasSubmitErrors)
                  ) {
                    toast.error({ content: errorContent, toastId })
                    setJustSubmitted(false)
                    setToastId(undefined)
                  }
                }}
              />
            )}

            {submitError && (
              <div role="alert" className="text-alert-primary mb-4">
                {submitError}
              </div>
            )}

            {/* Form fields supplied as children are rendered here */}
            <div className="">{children}</div>

            {/* This is  really nice to watch the form values change */}
            {/* <FormSpy
              subscription={{
                values: true,
              }}
              onChange={(props) => {
                console.log(`!! FORM VALUES`, props.values)
              }}
            /> */}
            {submitText && (
              <ButtonSection $buttonPosition={buttonPosition} $buttonFill={buttonFill}>
                <SButton
                  onClick={handleSubmit}
                  className={`w-full text-center py-2 flex flex-row items-center justify-center`}
                  {...{ variant: "juicy", ...buttonProps }}
                  type="submit"
                  aria-label={submitText}
                  // load={submitting}
                >
                  {submitText}
                </SButton>
              </ButtonSection>
            )}
          </form>
        )}
      />
    </FormDisabledProvider>
  )
}

export default Form
